<i18n locale="en" lang="json">
{
  "time": "Time",
  "invalidTime": "This is not a valid time. Format is {format}."
}
</i18n>
<i18n locale="nl" lang="json">
{
  "time": "Tijd",
  "invalidTime": "Dit is geen geldige tijd. Formaat is {format}."
}
</i18n>

<template>
  <text-field
    v-model.trim="userInputValue"
    :outlined="true"
    v-bind="$attrs"
    :label="label || t('time')"
    :validation="mergedValidationModels"
    :disabled="$attrs.disabled"
    :placeholder="timeFormat"
    :test-id="[componentName, testId || getModelExpression()]"
    @click="onClick"
    @focus="onFocus"
    @blur="onBlurUserInput"
    @keydown.up="doStep('up')"
    @keydown.down="doStep('down')"
  >
    <template #prepend-inner>
      <input
        ref="nativeTimeField"
        v-model="internalValue"
        type="time"
        :min="min"
        :max="max"
        :step="minuteStep * 60 * 1000"
        tabindex="-1"
        style="
          position: absolute;
          visibility: hidden;
          opacity: 1;
          width: 1px;
          height: 1px;
        "
        class="mt-10 ml-md-2"
        @update:model-value="onTimePickerChange"
      />
    </template>
    <template v-if="!$attrs.disabled" #append-inner>
      <button
        v-if="$attrs.clearable && !!userInputValue"
        v-set-test-id="[componentName, testId || getModelExpression(), 'clear']"
        class="mr-2"
        @click.prevent="userInputValue = undefined"
      >
        <font-awesome-icon :icon="faTimesCircle" />
      </button>
      <button
        v-set-test-id="[
          componentName,
          testId || getModelExpression(),
          'open-menu',
        ]"
        class="mr-2"
        @click.prevent="showPicker"
      >
        <font-awesome-icon :icon="faClock" />
      </button>
    </template>
  </text-field>
</template>

<script lang="ts" setup>
import { faClock, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { helpers } from '@vuelidate/validators';
import type { BaseValidation } from '@vuelidate/core';
import useVuelidate from '@vuelidate/core';
import { Duration } from 'luxon';
import customDebounce from '@/common/debounce';
import useIsMobile from '@/common/composition/useIsMobile';
import type { FormFieldEmits } from '@/common/components/form/FormField.vue';
import { useValue } from '@/common/components/form/FormField.vue';
import zeroPrefill from '@/common/zeroPrefill';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import {
  vSetTestId,
  getModelExpression,
} from '@/common/components/ActionableComponent';

const componentName = 'TimeField';

type Props = ActionableComponentProps & {
  label?: string;
  modelValue?: string;
  debounce?: number;
  validation?: BaseValidation;
  min?: string;
  max?: string;
  minuteStep?: number;
};
const props = withDefaults(defineProps<Props>(), { minuteStep: 1 });
type Emits = FormFieldEmits<string>;
const emit = defineEmits<Emits>();

const { t, locale } = useI18n();
const { d } = useI18n({ useScope: 'global' });
const isMobile = useIsMobile();
let isFocussed = false;

const nativeTimeField = ref<
  HTMLInputElement & { showPicker: () => undefined }
>();
const internalValue = useValue<string>(props, emit);

const regexValidTime12 =
  /^(1[0-2]|0?[1-9])[^\dam]+([0-5]?[0-9])[^\dam]*(●?[ap]m?)$/i;
const regexValidTime24 = /^(2[0-3]|[01]?[0-9])[^\dam]+([0-5]?[0-9])$/;
const localeDateOutput = computed(() =>
  d(new Date('2022-07-05 06:04:11'), 'long'),
);
const hasMeridiem = computed(() => localeDateOutput.value.includes('AM'));
const timeFormat = computed(() => (hasMeridiem.value ? 'HH:MM AM' : 'HH:MM'));

const convertIsoToMeridiemTime = (time: string) => {
  const timeParts = time.split(':');
  const hours = Number(timeParts[0]);
  const PM = hours > 11;
  const meridiemHours = PM ? hours - 12 : hours;
  return `${zeroPrefill(meridiemHours)}:${timeParts[1]} ${PM ? 'PM' : 'AM'}`;
};

const convertIsoTimeToFormat = (time: string | undefined) => {
  if (!time) {
    return undefined;
  }
  if (hasMeridiem.value) {
    return convertIsoToMeridiemTime(time);
  }
  return time;
};

const userInputValue = ref<string | undefined>(
  convertIsoTimeToFormat(props.modelValue),
);

const validationRules = {
  userInputValue: {
    timeIncorrect: helpers.withMessage(
      String(
        t('invalidTime', {
          format: timeFormat.value,
        }),
      ),
      () => !userInputValue.value || Boolean(internalValue.value),
    ),
  },
};
const v$ = useVuelidate(validationRules, { userInputValue }, { $scope: false });
const mergedValidationModels = computed(() => {
  if (!props.validation) {
    return v$.value;
  }
  return {
    ...props.validation,
    $touch: (): void => {
      props.validation?.$touch();
      v$.value.$touch();
    },
    $errors: [...v$.value.$errors, ...props.validation.$errors],
  };
});

const validTime = computed(() => {
  if (!userInputValue.value) {
    return undefined;
  }
  const match = userInputValue.value.match(
    hasMeridiem.value ? regexValidTime12 : regexValidTime24,
  );
  if (!match) {
    return undefined;
  }
  const inputHours = Number(match[1]);
  const isPM = hasMeridiem.value && match[3]?.toUpperCase().includes('P');
  const hours = zeroPrefill(isPM ? inputHours + 12 : inputHours);
  const minutes = zeroPrefill(Number(match[2]));
  return `${hours}:${minutes}`;
});

watch(internalValue, newValue => {
  if (!isFocussed) {
    userInputValue.value = convertIsoTimeToFormat(newValue);
  }
});

watch(validTime, newTime => {
  if (isFocussed) {
    internalValue.value = newTime;
  }
});

watch(locale, () => {
  userInputValue.value = convertIsoTimeToFormat(internalValue.value);
});

const showPicker = customDebounce(() => {
  if (!nativeTimeField.value) {
    throw new Error('nativeDateField not found');
  }
  try {
    nativeTimeField.value.showPicker();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
}, 150);

const onClick = () => {
  if (isMobile) {
    showPicker();
  }
};

const onFocus = (event: FocusEvent) => {
  isFocussed = true;
  if (isMobile) {
    showPicker();
    return;
  }
  if (event.currentTarget) {
    (event.currentTarget as HTMLInputElement).select();
  }
};

const onBlurUserInput = () => {
  isFocussed = false;
  if (validTime.value) {
    userInputValue.value = convertIsoTimeToFormat(validTime.value);
  }
};

const onTimePickerChange = () => {
  userInputValue.value = convertIsoTimeToFormat(internalValue.value);
};

const doStep = (direction: 'up' | 'down') => {
  if (!validTime.value) {
    return;
  }
  let duration = Duration.fromISOTime(validTime.value)
    .plus({ minutes: props.minuteStep * (direction === 'up' ? 1 : -1) })
    .normalize();
  if (duration.get('hours') >= 24) {
    duration = duration.minus({ hours: 24 });
  }
  if (duration.get('minute') < 0) {
    duration = duration.plus({ hours: 24 });
  }
  userInputValue.value = convertIsoTimeToFormat(duration.toFormat('hh:mm'));
};
</script>
